import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import ApiClient from '../../methods/api/apiClient';
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import loader from '../../methods/loader';
import { decryptId } from '../../components/common/Encryption/encryption';

const RequestGroup = ({ eventDetail = '', data = '', setRequestGroup = '', connectMeetLabel = 'ConnectMeet' }: any) => {
  const { eventid } = useParams();
  const user = useSelector((state: any) => state.user);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedCheckbox, setSelectedCheckbox] = useState("");
  const [selectRequiredError, setSelectRequiredError] = useState(false);
  const [acList, setAcList] = useState<any[]>([]);
  const [id, setId] = useState<string | null>(null);
  useEffect(() => {
      if (eventid) {
          decryptId(eventid).then(decrypted => setId(decrypted));
      }
  }, [eventid]);
  const groupId = data?.groupId?._id;

  // cw: Fetch user's Already Connected list
  useEffect(() => {
    if (user?._id && groupId) {
      ApiClient.get(`api/exclusions/list?groupId=${groupId}&userId=${user._id}`).then((res: any) => {
        if (res?.success && res?.data) {
          setAcList(res.data);
        }
      }).catch(() => {});
    }
  }, [user?._id, groupId]);

  // cw: Load existing request so user sees their previous selections
  useEffect(() => {
    if (user?.email && id && groupId) {
      ApiClient.get(`api/group-request/list?eventId=${id}&requestedBy=${user.email}&groupId=${groupId}`).then((res: any) => {
        if (res?.success && res?.data?.length > 0) {
          const existing = res.data[0];
          if (existing.requestType) {
            setSelectedCheckbox(existing.requestType);
          }
          if (existing.attendeesDetails?.length > 0) {
            const ids = existing.attendeesDetails
              .filter((a: any) => a._id)
              .map((a: any) => a._id.toString());
            if (ids.length > 0) setSelectedIds(ids);
          }
        }
      }).catch(() => {});
    }
  }, [user?.email, id, groupId]);

  // Show all attendees, not just those who RSVP'd Yes
  const allAttendees = [...(eventDetail('Yes') || []), ...(eventDetail('Pending') || []), ...(eventDetail('No') || [])];
  
  // Remove duplicates by id
  const uniqueAttendees = allAttendees.filter((itm: any, index: number, self: any[]) => 
    index === self.findIndex((t: any) => (t.id || t._id) === (itm.id || itm._id))
  );
  
  const options = uniqueAttendees
    .filter((itm: any) => ((itm.memberDetails?.email || itm?.email) !== user?.email))
    .map((itm: any) => {
      const memberId = itm.memberDetails?._id || itm.userId;
      return {
        value: itm.id || itm._id,
        label: itm.memberDetails?.fullName || itm?.fullName || 'Guest',
        userId: memberId,
      };
    })
    .sort((a: any, b: any) => (a.label || '').toLowerCase().localeCompare((b.label || '').toLowerCase()));

  // cw: Check if a member is on the user's AC list (by userId)
  const getAcExclusion = (userId: string) => {
    if (!userId) return null;
    return acList.find((excl: any) =>
      excl.excludedUserId?._id?.toString() === userId?.toString() ||
      excl.excludedUserId?.toString() === userId?.toString()
    );
  };

  const handleSelectChange = (selectedOptions: any, actionMeta: any) => {
    // Check if a newly added member is on the AC list
    if (actionMeta?.action === 'select-option' && actionMeta?.option) {
      const newId = actionMeta.option.value;
      const newUserId = actionMeta.option.userId;
      const excl = newUserId ? getAcExclusion(newUserId) : null;
      if (excl) {
        const memberName = actionMeta.option.label;
        Swal.fire({
          title: 'Already Connected',
          text: `${memberName} is on your Already Connected list.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Remove From AC',
          cancelButtonText: 'Cancel Request',
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
        }).then((result) => {
          if (result.isConfirmed) {
            // Remove from AC list then add to selection
            ApiClient.put('api/exclusions/remove', { id: excl._id, userId: user._id }).then((res: any) => {
              if (res?.success) {
                setAcList(acList.filter((e: any) => e._id !== excl._id));
                setSelectedIds(selectedOptions.map((o: any) => o.value));
                toast.success('Removed from Already Connected list');
              } else {
                toast.error(res?.message || 'Failed to remove from AC list');
                // Remove the member from selection since AC removal failed
                setSelectedIds(selectedOptions.filter((o: any) => o.value !== newId).map((o: any) => o.value));
              }
            }).catch(() => {
              toast.error('Failed to remove from AC list');
              setSelectedIds(selectedOptions.filter((o: any) => o.value !== newId).map((o: any) => o.value));
            });
          } else {
            // User cancelled - remove the member from selection
            setSelectedIds(selectedOptions.filter((o: any) => o.value !== newId).map((o: any) => o.value));
          }
        });
        return; // Don't update selectedIds yet, wait for Swal result
      }
    }

    const selectedIdsArray = selectedOptions.map((option: any) => option.value);
    setSelectedIds(selectedIdsArray);
    setSelectRequiredError(false);
  };

  const handleSubmit = () => {
    if (selectedCheckbox === "connect_with_members" && selectedIds.length === 0) {
      setSelectRequiredError(true);
      return;
    }

    loader(true);
    let payload = {
      eventId: id,
      groupId: data?.groupId?._id,
      requestedBy: user?.email,
      attendeeIds: selectedIds,
      userId: user?._id,
      requestType: selectedCheckbox
    };

    ApiClient.post("api/group/request", payload).then(res => {
      if (res) {
        toast(res.message);
        loader(false);
        setRequestGroup(false);
      }
    });
  };

  const handleCheckboxChange = (checkboxName: string) => {
    setSelectedCheckbox(checkboxName);
  };

  const isSubmitDisabled = !selectedCheckbox; // Disable if no checkbox is selected

  return (
    <div className='flex flex-col gap-4 mt-4'>
      <div>
        <div className='flex gap-2 items-center'>
          <input
            className='h-4 w-4 cursor-pointer'
            type="radio"
            checked={selectedCheckbox === "connect_with_members"}
            onChange={() => handleCheckboxChange("connect_with_members")}
          />
          <span>Request specific people for my {connectMeetLabel} for this event</span>
        </div>
        {selectedCheckbox === "connect_with_members" && (
          <div className='mt-2'>
            <Select
              isMulti
              options={options}
              value={options.filter((o: any) => selectedIds.includes(o.value))}
              placeholder="Select Members"
              className='capitalize '
              onChange={handleSelectChange}
            />
            {selectRequiredError && <p className="text-red-500">Please select at least one member.</p>}
          </div>
        )}
      </div>

      <div>
        <div className='flex gap-2 items-center'>
          <input
            className='h-4 w-4 cursor-pointer'
            type="radio"
            checked={selectedCheckbox === "not_interested"}
            onChange={() => handleCheckboxChange("not_interested")}
          />
          <span>I'm NOT available for a {connectMeetLabel} for this event</span>
        </div>
      </div>

      <div>
        <div className='flex gap-2 items-center'>
          <input
            className='h-4 w-4 cursor-pointer'
            type="radio"
            checked={selectedCheckbox === "system_generate"}
            onChange={() => handleCheckboxChange("system_generate")}
          />
          <span>I'm available for a {connectMeetLabel} for this event</span>
        </div>
      </div>
    
      <div className='mt-4 flex items-center justify-end'>
        <button
          className='bg-[#EF7A2B] inline-flex py-2 px-6 flex items-center justify-center gap-x-2 text-white shadow hover:shadow-lg rounded-xl'
          onClick={handleSubmit}
          disabled={isSubmitDisabled} // Disable if no checkbox is selected
        >
          Submit My Request
        </button>
      </div>
    </div>
  );
};

export default RequestGroup;
